import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDatePicker, RaaDialog, RaaModalPage, RaaSelect, RaaSpinner } from 'raa-react';

import { SpinnerContainer } from 'components/drawerSections/DrawerSections';
import RaaInputField from 'components/fieldComponents/raaInputField/RaaInputField';
import RaaTextAreaField from 'components/fieldComponents/raaTextAreaField/RaaTextAreaField';
import ImageFileUploadInput, { ValidFileMimeTypesEnum } from 'components/utkast/imageFileUpload/ImageFileUpload';
import { BildPostFormValues, FotografiFormSchema } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import { Dokumentationslicenstyp } from 'models';

import { DokumentationstypEnum, FotoVyEnum } from 'enums';

import { usePostBildMutation, usePutBildMutation } from 'api/endpoints/dokumentationBildSlice';
import { dokumentationSlice } from 'api/endpoints/dokumentationSlice';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { setApiValidationErrors } from 'utilities/formUtilities';
import { isFetchBaseQueryModelError } from 'utilities/typeGuards';

interface FotografiModalProps {
  resursId?: string;
  titel?: string;
  bildtext?: string;
  file: File;
  utkastId: string;
  height: number;
  width: number;
  vy?: FotoVyEnum;
  licens?: number;
  upphovsmanFornamn?: string;
  upphovsmanEfternamn?: string;
  date?: Date;
  onSubmit?: () => void;
  onDelete?: (resursId: string, titel: string) => void;
  onCancel?: () => void;
}

const FotografiModal: FC<FotografiModalProps> = ({
  resursId,
  titel,
  bildtext,
  file,
  utkastId,
  height,
  width,
  vy,
  licens = 3,
  upphovsmanFornamn,
  upphovsmanEfternamn,
  date,
  onSubmit,
  onDelete,
  onCancel
}) => {
  const dispatch = useAppDispatch();
  const { setDialog, unsetDialog, clearDialogStack } = useDialog();

  const formikHelpersRef = useRef<FormikHelpers<BildPostFormValues>>();

  const [srcPreview, setSrcPreview] = useState('');
  const [imageInfo, setImageInfo] = useState({ height: 0, width: 0, filename: '', size: 0 });
  const [initialValues, setInitialValues] = useState<BildPostFormValues>();
  const [selectedDokumentationsLicenstyp, setSelectedDokumentationsLicenstyp] = useState<Dokumentationslicenstyp>();
  const [selectedFile, setSelectedFile] = useState(file);

  const dokumentationlicenstyper = useAppSelector(
    (state: RootState) => state.bebyggelseobjektDomaner.data?.dokumentationlicenstyper
  );

  const bildlicenser = useMemo(
    () => dokumentationlicenstyper?.filter((licens) => licens.dokumentationtyp === 'BILD'),
    [dokumentationlicenstyper]
  );

  const [
    putUtkastBild,
    { isSuccess: putUtkastBildIsSuccess, isLoading: putUtkastBildIsLoading, error: putUtkastBildError }
  ] = usePutBildMutation();

  const [
    postUtkastBild,
    { isSuccess: postUtkastBildIsSuccess, isLoading: postUtkastBildIsLoading, error: postUtkastBildError }
  ] = usePostBildMutation();

  const { requestStatus: utkastRequestStatus } = useAppSelector((state: RootState) => state.utkast);

  const resetDokumentationsLicenstypSelect = useCallback(() => {
    if (bildlicenser) {
      const selectedLicens = bildlicenser.find((dl: Dokumentationslicenstyp) => dl.id === licens);
      setSelectedDokumentationsLicenstyp(selectedLicens);
    }
  }, [bildlicenser, licens]);

  useEffect(() => {
    if (!formikHelpersRef.current) return;

    if (postUtkastBildIsSuccess || putUtkastBildIsSuccess) {
      dispatch(dokumentationSlice.util.invalidateTags(['Dokumentation']));
      formikHelpersRef.current.resetForm();
      onSubmit && onSubmit();
    }
  }, [onSubmit, putUtkastBildIsSuccess, postUtkastBildIsSuccess, dispatch]);

  useEffect(() => {
    const setFieldError = formikHelpersRef.current?.setFieldError;

    if (postUtkastBildError && setFieldError && isFetchBaseQueryModelError(postUtkastBildError)) {
      setApiValidationErrors(postUtkastBildError.data, setFieldError);
    }

    if (putUtkastBildError && setFieldError && isFetchBaseQueryModelError(putUtkastBildError)) {
      setApiValidationErrors(putUtkastBildError.data, setFieldError);
    }
  }, [putUtkastBildError, postUtkastBildError]);

  useEffect(() => {
    const setInitialFormValues = () => {
      setInitialValues({
        titel: titel || '',
        bildfil: selectedFile || undefined,
        bildtext: bildtext || '',
        licens: licens || 3,
        tillkomstdatum: date || new Date(),
        upphovsmanFornamn: upphovsmanFornamn || '',
        upphovsmanEfternamn: upphovsmanEfternamn || '',
        vy: vy || FotoVyEnum.EXTERIOR
      });
    };

    const src = URL.createObjectURL(selectedFile);
    setSrcPreview(src);
    setImageInfo({ height, width, filename: selectedFile.name, size: selectedFile.size });
    setInitialFormValues();
  }, [selectedFile, height, width, date, titel, bildtext, licens, upphovsmanFornamn, upphovsmanEfternamn, vy]);

  useEffect(() => {
    if (dokumentationlicenstyper) {
      resetDokumentationsLicenstypSelect();
    }
  }, [dokumentationlicenstyper, resetDokumentationsLicenstypSelect]);

  const submitForm = (values: BildPostFormValues, formikHelpers: FormikHelpers<BildPostFormValues>) => {
    formikHelpersRef.current = formikHelpers;

    // Force current selected file
    values.bildfil = selectedFile;

    if (resursId) {
      putUtkastBild({ bildPut: values, utkastId, resursId });
    } else {
      postUtkastBild({ bildPost: values, utkastId });
    }
  };

  const deleteImage = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (onDelete && resursId) {
      onDelete(resursId, titel || '');
    }
  };

  const showWarningDialog = ({ proceed, cancel }: { proceed: () => void; cancel: () => void }) => {
    setDialog(RaaDialog, {
      cancelButtonText: 'avbryt',
      proceedButtonText: 'släng',
      header: 'Vill du slänga ändringarna?',
      proceed,
      cancel
    });
  };

  return (
    <RaaModalPage header="Fotografi">
      <SpinnerContainer showSpinner={!dokumentationlicenstyper}>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validationSchema={FotografiFormSchema}
            onSubmit={async (values: BildPostFormValues, formikHelpers: FormikHelpers<BildPostFormValues>) =>
              submitForm(values, formikHelpers)
            }
          >
            {({ setFieldValue, values, errors, touched, setFieldTouched, dirty }) => (
              <Form id="bildForm" className="tw-flex tw-flex-col">
                <fieldset
                  className="tw-grid tw-grid-cols-3 tw-border-b tw-border-raa-gray-3 tw-px-4 tw-pb-4 md:tw-px-6 md:tw-pb-6"
                  disabled={utkastRequestStatus === 'pending' || putUtkastBildIsLoading || postUtkastBildIsLoading}
                >
                  <legend className="tw-sr-only">Redigera bildens metadata</legend>
                  <div className="tw-col-span-3 tw-mb-4 md:tw-col-span-1 md:tw-mb-0">
                    <div className="tw-mx-2 tw-mb-4">
                      <img src={srcPreview} alt="Min bild" />
                    </div>
                    <div className="tw-mx-2 tw-mb-6 tw-break-words">
                      <p>{imageInfo.filename}</p>
                      <p>{`${imageInfo.height} x ${imageInfo.width} pixlar`}</p>
                      <p>{`${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}</p>
                    </div>
                    <div className="tw-mx-2">
                      <ImageFileUploadInput
                        displayAsButton={true}
                        name="bildfil"
                        labelText="Byt bildfil"
                        dokumentationstyp={DokumentationstypEnum.BILD}
                        validFileMimeTypes={[ValidFileMimeTypesEnum.IMAGE_JPEG, ValidFileMimeTypesEnum.IMAGE_PNG]}
                        uploading={() => {
                          setDialog(RaaDialog, {
                            header: 'Laddar upp fil',
                            ariaDescribedBy: 'dialog-description',
                            children: (
                              <div className="tw-flex tw-justify-center tw-py-12">
                                <RaaSpinner size="3rem" />
                                <span id="dialog-description" className="tw-ml-2 tw-self-center">
                                  Vänta...
                                </span>
                              </div>
                            )
                          });
                        }}
                        success={(newFile: File, height: number, width: number) => {
                          unsetDialog();
                          setSelectedFile(newFile);
                          const src = URL.createObjectURL(newFile);
                          setSrcPreview(src);
                          setImageInfo({ height, width, filename: newFile.name, size: newFile.size });
                        }}
                      ></ImageFileUploadInput>
                    </div>
                    <p className="tw-mt-6 tw-text-raa-error-red">
                      {errors.bildfil && touched.bildfil && errors.bildfil}
                    </p>
                  </div>
                  <div className="tw-col-span-3 md:tw-col-span-2 md:tw-ml-4">
                    <div className="tw-mx-2 tw-mb-4">
                      <RaaInputField
                        name="titel"
                        placeholder=""
                        label="Titel (t.ex. namn och väderstreck) *"
                        shouldValidateForm={true}
                      />
                    </div>
                    <div className="tw-mx-2 tw-mb-4">
                      <RaaTextAreaField
                        label="Bildtext"
                        multiple
                        name="bildtext"
                        classString="raa-textarea"
                        placeholder="Beskriv vad bilden föreställer"
                      />
                    </div>
                    <fieldset className="tw-mx-2 tw-mb-4">
                      <legend className="raa-label">Vy</legend>
                      <div className="tw-flex">
                        <div className="tw-mr-6 tw-flex tw-items-center">
                          <RaaInputField
                            classString=" " // Disables default style
                            type="radio"
                            value={FotoVyEnum.EXTERIOR}
                            id="vy_exterior"
                            name="vy"
                            checked={values?.vy === FotoVyEnum.EXTERIOR}
                            showError={false}
                            shouldValidateForm={true}
                          />
                          <label htmlFor="vy_exterior" className="tw-ml-2">
                            Exteriör
                          </label>
                        </div>
                        <div className="tw-flex tw-items-center">
                          <RaaInputField
                            classString=" " // Disables default style
                            id="vy_interior"
                            value={FotoVyEnum.INTERIOR}
                            type="radio"
                            checked={values?.vy === FotoVyEnum.INTERIOR}
                            name="vy"
                            showError={false}
                            shouldValidateForm={true}
                          />
                          <label htmlFor="vy_interior" className="tw-ml-2">
                            Interiör
                          </label>
                        </div>
                        <p className="tw-mt-1 tw-text-raa-error-red">{errors.vy && touched.vy && errors.vy}</p>
                      </div>
                    </fieldset>
                    {bildlicenser && (
                      <div className="tw-mx-2 tw-mb-4">
                        <label className="raa-label" htmlFor="licens">
                          Licens *
                        </label>
                        <RaaSelect
                          disableFiltration={true}
                          inputElementId="licens"
                          value={selectedDokumentationsLicenstyp}
                          domain={bildlicenser}
                          displayAttr="namn"
                          onSelect={async (value: Dokumentationslicenstyp) => {
                            await setFieldValue('licens', value.id, true);
                            setSelectedDokumentationsLicenstyp(value);
                          }}
                          placeholder="Välj en licens"
                        />
                        <p className="tw-mt-1 tw-text-raa-error-red">
                          {errors.licens && touched.licens && errors.licens}
                        </p>
                      </div>
                    )}
                    {selectedDokumentationsLicenstyp?.id !== 2 && (
                      <fieldset className="tw-mx-2 tw-mb-4">
                        <legend className="raa-label">Fotograf</legend>
                        <div className="tw-flex tw-flex-wrap tw-gap-4">
                          <div className="tw-grow">
                            <label className="tw-sr-only" htmlFor="upphovsmanFornamn">
                              Förnamn
                            </label>
                            <RaaInputField
                              id="upphovsmanFornamn"
                              name="upphovsmanFornamn"
                              placeholder="Förnamn"
                              shouldValidateForm={true}
                            />
                          </div>
                          <div className="tw-grow">
                            <label className="tw-sr-only" htmlFor="upphovsmanEfternamn">
                              Efternamn
                            </label>
                            <RaaInputField
                              id="upphovsmanEfternamn"
                              name="upphovsmanEfternamn"
                              placeholder="Efternamn"
                              shouldValidateForm={true}
                            />
                          </div>
                        </div>
                      </fieldset>
                    )}
                    <div className="tw-mx-2">
                      <label className="raa-label" htmlFor="tillkomstdatum">
                        Fotografiet taget *
                      </label>
                      <div>
                        <RaaDatePicker
                          id="tillkomstdatum"
                          defaultValue={dayjs(date)}
                          clearable={false}
                          onChange={async (value) => {
                            const dayjsValue = dayjs(value, 'YYYY-MM-DD').toString();
                            await setFieldTouched('tillkomstdatum');
                            await setFieldValue('tillkomstdatum', dayjsValue, true);
                          }}
                          disableFuture={true}
                        />
                      </div>
                      <p className="tw-mt-1 tw-text-raa-error-red">
                        <>{errors.tillkomstdatum && touched.tillkomstdatum && errors.tillkomstdatum}</>
                      </p>
                    </div>
                  </div>
                </fieldset>

                <fieldset
                  className="tw-mt-6 tw-flex tw-flex-col tw-justify-between tw-px-4 tw-pb-4 md:tw-flex-row md:tw-px-6 md:tw-pb-6"
                  disabled={utkastRequestStatus === 'pending' || putUtkastBildIsLoading || postUtkastBildIsLoading}
                >
                  <legend className="tw-sr-only">Spara eller ångra ändringar</legend>
                  <button
                    type="button"
                    className="raa-button raa-button-cancel"
                    onClick={() => {
                      if (dirty) {
                        showWarningDialog({
                          proceed: () => {
                            onCancel && onCancel();
                            clearDialogStack();
                          },
                          cancel: () => {
                            unsetDialog();
                          }
                        });
                      } else {
                        onCancel && onCancel();
                        unsetDialog();
                      }
                    }}
                  >
                    avbryt
                  </button>
                  <div className="tw-flex tw-flex-col tw-gap-x-3 md:tw-flex-row">
                    <button
                      className={classNames('raa-button', { 'tw-hidden': !resursId })}
                      type="button"
                      onClick={(e) => deleteImage(e)}
                    >
                      Radera bilden
                    </button>
                    <button type="submit" className="raa-button raa-button-confirm tw-mt-2 md:tw-mt-0" form="bildForm">
                      {utkastRequestStatus === 'pending' || putUtkastBildIsLoading || postUtkastBildIsLoading ? (
                        <RaaSpinner size="1.5rem" />
                      ) : (
                        'spara'
                      )}
                    </button>
                  </div>
                </fieldset>
              </Form>
            )}
          </Formik>
        )}
      </SpinnerContainer>
    </RaaModalPage>
  );
};

export default FotografiModal;
